import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChatNotification } from '../../../contexts/ChatNotificationContext';
import { fetchChatHistoricoAdmin, fetchChatEstatisticasAdmin, fetchChatUsuarios, fetchChatCanais } from '../../../services/api';
import styles from './ChatHistoricoAdmin.module.css';

// ============================================================
// HELPERS
// ============================================================
const AVATAR_CORES = [
  'linear-gradient(135deg, #6366f1, #8b5cf6)',
  'linear-gradient(135deg, #f43f5e, #ec4899)',
  'linear-gradient(135deg, #06b6d4, #3b82f6)',
  'linear-gradient(135deg, #10b981, #059669)',
  'linear-gradient(135deg, #f59e0b, #d97706)',
  'linear-gradient(135deg, #8b5cf6, #d946ef)',
  'linear-gradient(135deg, #14b8a6, #06b6d4)',
  'linear-gradient(135deg, #ef4444, #f97316)',
];
const getAvatarCor = (id) => AVATAR_CORES[(id || 0) % AVATAR_CORES.length];
const getIniciais = (nome) => {
  if (!nome) return '?';
  const p = nome.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : nome.substring(0, 2).toUpperCase();
};

const parseUTC = (s) => {
  if (!s) return null;
  let str = String(s).trim();
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('T')) str = str.replace(' ', 'T') + 'Z';
  else if (!str.endsWith('Z') && str.includes('T') && !str.includes('+')) str += 'Z';
  return new Date(str);
};

const fmtHora = (s) => {
  const d = parseUTC(s);
  if (!d || isNaN(d)) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const fmtData = (s) => {
  const d = parseUTC(s);
  if (!d || isNaN(d)) return '';
  const hoje = new Date();
  const ontem = new Date(hoje); ontem.setDate(ontem.getDate() - 1);
  if (d.toDateString() === hoje.toDateString()) return 'Hoje';
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtDataISO = (d) => d?.toISOString()?.split('T')[0] ?? '';

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const ChatHistoricoAdmin = () => {
  const { socketRef } = useChatNotification();

  // Dados principais
  const [mensagens, setMensagens] = useState([]);
  const [temMais, setTemMais] = useState(false);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [estatisticas, setEstatisticas] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [canais, setCanais] = useState([]);

  // Filtros
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroCanal, setFiltroCanal] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroInicio, setFiltroInicio] = useState('');
  const [filtroFim, setFiltroFim] = useState('');

  // Imagem em zoom
  const [imagemZoom, setImagemZoom] = useState(null);

  // Aba ativa: 'historico' | 'estatisticas'
  const [aba, setAba] = useState('historico');

  // Indicador de novas mensagens em tempo real
  const [novasEmTempoReal, setNovasEmTempoReal] = useState(0);
  const topo = useRef(null);

  // --------------------------------------------------------
  // CARREGAR DADOS INICIAIS
  // --------------------------------------------------------
  const carregarUsuariosECanais = useCallback(async () => {
    const [ru, rc] = await Promise.all([fetchChatUsuarios(), fetchChatCanais()]);
    if (ru.success) setUsuarios(ru.data);
    if (rc.success) setCanais(rc.data);
  }, []);

  const carregarEstatisticas = useCallback(async () => {
    const r = await fetchChatEstatisticasAdmin();
    if (r.success) setEstatisticas(r.data);
  }, []);

  const carregarHistorico = useCallback(async (antes = null, acumular = false) => {
    setCarregando(true);
    const r = await fetchChatHistoricoAdmin({
      canal_id:    filtroCanal   || null,
      usuario_id:  filtroUsuario || null,
      data_inicio: filtroInicio  || null,
      data_fim:    filtroFim     || null,
      busca:       filtroBusca   || null,
      tipo:        filtroTipo    || null,
      antes,
      limit: 50,
    });
    if (r.success) {
      setMensagens(prev => acumular ? [...r.data, ...prev] : r.data);
      setTemMais(r.temMais);
      setTotal(r.total);
    }
    setCarregando(false);
  }, [filtroCanal, filtroUsuario, filtroInicio, filtroFim, filtroBusca, filtroTipo]);

  // Carregar tudo no mount
  useEffect(() => {
    carregarUsuariosECanais();
    carregarEstatisticas();
  }, [carregarUsuariosECanais, carregarEstatisticas]);

  useEffect(() => {
    carregarHistorico();
    setNovasEmTempoReal(0);
  }, [carregarHistorico]);

  // --------------------------------------------------------
  // SOCKET — escutar novas mensagens em tempo real
  // --------------------------------------------------------
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const onNova = (msg) => {
      // Se não há filtros ativos, adicionar direto; caso contrário, apenas avisar
      const semFiltro = !filtroCanal && !filtroUsuario && !filtroInicio && !filtroFim && !filtroBusca && !filtroTipo;
      if (semFiltro) {
        setMensagens(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        setTotal(t => t + 1);
      } else {
        setNovasEmTempoReal(n => n + 1);
      }
    };

    const onEditada = (upd) => {
      setMensagens(prev => prev.map(m => m.id === upd.id ? { ...m, ...upd } : m));
    };

    const onDeletada = ({ id }) => {
      setMensagens(prev => prev.filter(m => m.id !== id));
      setTotal(t => Math.max(0, t - 1));
    };

    socket.on('nova-mensagem', onNova);
    socket.on('mensagem-editada', onEditada);
    socket.on('mensagem-deletada', onDeletada);

    return () => {
      socket.off('nova-mensagem', onNova);
      socket.off('mensagem-editada', onEditada);
      socket.off('mensagem-deletada', onDeletada);
    };
  }, [socketRef, filtroCanal, filtroUsuario, filtroInicio, filtroFim, filtroBusca, filtroTipo]);

  // --------------------------------------------------------
  // AGRUPAMENTOS
  // --------------------------------------------------------
  // Agrupar mensagens por data → usuário consecutivo
  const construirAgrupado = () => {
    const grupos = []; // [{ data, autor_id, autor_nome, mensagens[] }]
    let diaAtual = '';
    let grupoAtual = null;

    for (const msg of mensagens) {
      const dia = fmtData(msg.created_at);
      if (dia !== diaAtual) {
        diaAtual = dia;
        grupoAtual = null;
      }
      if (!grupoAtual || grupoAtual.autor_id !== msg.usuario_id || grupoAtual.dia !== dia) {
        grupoAtual = {
          dia,
          autor_id: msg.usuario_id,
          autor_nome: msg.autor_nome,
          autor_email: msg.autor_email,
          canal_nome: msg.canal_nome,
          canal_tipo: msg.canal_tipo,
          canal_icone: msg.canal_icone,
          eh_dm: msg.eh_dm,
          dm_user1_nome: msg.dm_user1_nome,
          dm_user2_nome: msg.dm_user2_nome,
          mensagens: [],
        };
        grupos.push(grupoAtual);
      }
      grupoAtual.mensagens.push(msg);
    }
    return grupos;
  };

  const agrupado = construirAgrupado();

  // Dias distintos para separadores
  const diasVistos = new Set();

  // --------------------------------------------------------
  // APLICAR FILTROS
  // --------------------------------------------------------
  const aplicarFiltros = () => {
    carregarHistorico();
    setNovasEmTempoReal(0);
  };

  const limparFiltros = () => {
    setFiltroUsuario('');
    setFiltroCanal('');
    setFiltroTipo('');
    setFiltroBusca('');
    setFiltroInicio('');
    setFiltroFim('');
  };

  // Hoje como data padrão para o fim
  const hoje = fmtDataISO(new Date());

  // --------------------------------------------------------
  // RENDER
  // --------------------------------------------------------
  return (
    <div className={styles.container}>
      {/* ========= CABEÇALHO ========= */}
      <div className={styles.cabecalho}>
        <div className={styles.cabecalhoInfo}>
          <div className={styles.titulo}>
            <span className={styles.tituloIcon}>🗂️</span>
            Histórico Geral do Chat
          </div>
          <div className={styles.subtitulo}>
            Visão administrativa de todas as conversas em tempo real
          </div>
        </div>

        {/* Abas */}
        <div className={styles.abas}>
          <button
            className={`${styles.aba} ${aba === 'historico' ? styles.abaAtiva : ''}`}
            onClick={() => setAba('historico')}
            id="aba-historico"
          >
            💬 Histórico
          </button>
          <button
            className={`${styles.aba} ${aba === 'estatisticas' ? styles.abaAtiva : ''}`}
            onClick={() => { setAba('estatisticas'); carregarEstatisticas(); }}
            id="aba-estatisticas"
          >
            📊 Estatísticas
          </button>
        </div>
      </div>

      {/* ========= ABA HISTÓRICO ========= */}
      {aba === 'historico' && (
        <div className={styles.layout}>
          {/* ----- PAINEL DE FILTROS ----- */}
          <aside className={styles.filtrosPanel}>
            <div className={styles.filtrosTitulo}>🔍 Filtros</div>

            {/* Busca textual */}
            <div className={styles.filtroGrupo}>
              <label className={styles.filtroLabel}>Buscar texto</label>
              <input
                id="filtro-busca"
                className={styles.filtroInput}
                type="text"
                placeholder="Palavra-chave..."
                value={filtroBusca}
                onChange={e => setFiltroBusca(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && aplicarFiltros()}
              />
            </div>

            {/* Usuário */}
            <div className={styles.filtroGrupo}>
              <label className={styles.filtroLabel}>Usuário</label>
              <select
                id="filtro-usuario"
                className={styles.filtroSelect}
                value={filtroUsuario}
                onChange={e => setFiltroUsuario(e.target.value)}
              >
                <option value="">Todos os usuários</option>
                {usuarios.map(u => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>

            {/* Canal */}
            <div className={styles.filtroGrupo}>
              <label className={styles.filtroLabel}>Canal</label>
              <select
                id="filtro-canal"
                className={styles.filtroSelect}
                value={filtroCanal}
                onChange={e => setFiltroCanal(e.target.value)}
              >
                <option value="">Todos os canais</option>
                {canais.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.tipo === 'dm' ? `💬 DM: ${c.dm_usuario?.nome || c.nome}` : `${c.icone || '💬'} #${c.nome}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo */}
            <div className={styles.filtroGrupo}>
              <label className={styles.filtroLabel}>Tipo de conversa</label>
              <select
                id="filtro-tipo"
                className={styles.filtroSelect}
                value={filtroTipo}
                onChange={e => setFiltroTipo(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="grupo">Canais de grupo</option>
                <option value="dm">Mensagens diretas</option>
              </select>
            </div>

            {/* Data início */}
            <div className={styles.filtroGrupo}>
              <label className={styles.filtroLabel}>Data início</label>
              <input
                id="filtro-data-inicio"
                className={styles.filtroInput}
                type="date"
                value={filtroInicio}
                max={hoje}
                onChange={e => setFiltroInicio(e.target.value)}
              />
            </div>

            {/* Data fim */}
            <div className={styles.filtroGrupo}>
              <label className={styles.filtroLabel}>Data fim</label>
              <input
                id="filtro-data-fim"
                className={styles.filtroInput}
                type="date"
                value={filtroFim}
                max={hoje}
                onChange={e => setFiltroFim(e.target.value)}
              />
            </div>

            <button
              id="btn-aplicar-filtros"
              className={styles.btnAplicar}
              onClick={aplicarFiltros}
              disabled={carregando}
            >
              {carregando ? '⏳ Buscando...' : '🔍 Aplicar filtros'}
            </button>
            <button
              id="btn-limpar-filtros"
              className={styles.btnLimpar}
              onClick={limparFiltros}
            >
              ✕ Limpar
            </button>

            {/* Contagem */}
            <div className={styles.totalBadge}>
              {total > 0 && (
                <span>{total.toLocaleString('pt-BR')} mensagem{total !== 1 ? 's' : ''}</span>
              )}
            </div>

            {/* Alerta de novas mensagens com filtros ativos */}
            {novasEmTempoReal > 0 && (
              <button
                className={styles.novasMsgBtn}
                onClick={() => { carregarHistorico(); setNovasEmTempoReal(0); }}
              >
                🔄 {novasEmTempoReal} nova{novasEmTempoReal > 1 ? 's' : ''} mensagem{novasEmTempoReal > 1 ? 'ns' : ''} — Atualizar
              </button>
            )}

            {/* Pulso de tempo real */}
            <div className={styles.rtIndicator}>
              <span className={styles.rtDot} />
              Atualização em tempo real ativa
            </div>
          </aside>

          {/* ----- FEED DE MENSAGENS ----- */}
          <main className={styles.feed} ref={topo}>
            {/* Carregar mais (topo) */}
            {temMais && (
              <div className={styles.carregarMaisWrap}>
                <button
                  className={styles.carregarMaisBtn}
                  onClick={() => carregarHistorico(mensagens[0]?.id, true)}
                  disabled={carregando}
                  id="btn-carregar-mais"
                >
                  {carregando ? 'Carregando...' : '⬆ Carregar mensagens anteriores'}
                </button>
              </div>
            )}

            {mensagens.length === 0 && !carregando && (
              <div className={styles.vazio}>
                <div className={styles.vazioIcon}>📭</div>
                <div className={styles.vazioTitulo}>Nenhuma mensagem encontrada</div>
                <div className={styles.vazioSub}>Tente ajustar os filtros acima</div>
              </div>
            )}

            {agrupado.map((grupo, gi) => {
              const separador = !diasVistos.has(grupo.dia);
              if (separador) diasVistos.add(grupo.dia);

              return (
                <React.Fragment key={gi}>
                  {/* Separador de data */}
                  {separador && (
                    <div className={styles.dateSep}>
                      <span className={styles.dateSepText}>{grupo.dia}</span>
                    </div>
                  )}

                  {/* Bloco do usuário */}
                  <div className={styles.blocoUsuario} id={`bloco-${gi}`}>
                    {/* Cabeçalho do bloco */}
                    <div className={styles.blocoHeader}>
                      <div
                        className={styles.avatar}
                        style={{ background: getAvatarCor(grupo.autor_id) }}
                      >
                        {getIniciais(grupo.autor_nome)}
                      </div>
                      <div className={styles.blocoHeaderInfo}>
                        <span className={styles.autorNome}>{grupo.autor_nome}</span>
                        <span className={styles.autorEmail}>{grupo.autor_email}</span>
                        <span className={styles.canalTag}>
                          {grupo.canal_tipo === 'dm'
                            ? `💬 DM entre ${grupo.dm_user1_nome || '?'} e ${grupo.dm_user2_nome || '?'}`
                            : `${grupo.canal_icone || '💬'} #${grupo.canal_nome}`
                          }
                        </span>
                      </div>
                    </div>

                    {/* Mensagens do bloco */}
                    <div className={styles.blocoMensagens}>
                      {grupo.mensagens.map(msg => (
                        <div key={msg.id} className={styles.mensagem} id={`msg-${msg.id}`}>
                          {/* Imagem */}
                          {msg.imagem && (
                            <img
                              src={msg.imagem}
                              alt="Imagem"
                              className={styles.msgImagem}
                              onClick={() => setImagemZoom(msg.imagem)}
                            />
                          )}

                          {/* Conteúdo */}
                          {msg.conteudo && msg.conteudo !== '[Imagem]' && (
                            <span className={styles.msgTexto}>{msg.conteudo}</span>
                          )}

                          {/* Meta */}
                          <span className={styles.msgHora}>
                            {fmtHora(msg.created_at)}
                            {msg.editada === 1 && <em className={styles.msgEditada}> (editada)</em>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

            {carregando && (
              <div className={styles.loading}>
                <div className={styles.loadingSpinner} />
                Carregando mensagens...
              </div>
            )}
          </main>
        </div>
      )}

      {/* ========= ABA ESTATÍSTICAS ========= */}
      {aba === 'estatisticas' && (
        <div className={styles.estatisticasArea}>
          {!estatisticas ? (
            <div className={styles.loading}><div className={styles.loadingSpinner} /> Carregando...</div>
          ) : (
            <>
              {/* Cards de resumo */}
              <div className={styles.statsCards}>
                <div className={styles.statCard} id="stat-mensagens">
                  <div className={styles.statIcon}>💬</div>
                  <div className={styles.statValor}>{(estatisticas.totalMensagens || 0).toLocaleString('pt-BR')}</div>
                  <div className={styles.statLabel}>Total de Mensagens</div>
                </div>
                <div className={styles.statCard} id="stat-canais">
                  <div className={styles.statIcon}>📢</div>
                  <div className={styles.statValor}>{estatisticas.totalCanais}</div>
                  <div className={styles.statLabel}>Canais de Grupo</div>
                </div>
                <div className={styles.statCard} id="stat-dms">
                  <div className={styles.statIcon}>📩</div>
                  <div className={styles.statValor}>{estatisticas.totalDMs}</div>
                  <div className={styles.statLabel}>Conversas Diretas</div>
                </div>
                <div className={styles.statCard} id="stat-usuarios">
                  <div className={styles.statIcon}>👥</div>
                  <div className={styles.statValor}>{estatisticas.totalUsuarios}</div>
                  <div className={styles.statLabel}>Usuários Ativos</div>
                </div>
              </div>

              <div className={styles.statsDetalhes}>
                {/* Top usuários */}
                <div className={styles.statsSecao}>
                  <div className={styles.statsSecaoTitulo}>🏆 Usuários Mais Ativos</div>
                  <div className={styles.rankingLista}>
                    {(estatisticas.porUsuario || []).map((u, i) => (
                      <div key={u.id} className={styles.rankingItem} id={`rank-user-${u.id}`}>
                        <span className={styles.rankingPos}>#{i + 1}</span>
                        <div
                          className={styles.rankingAvatar}
                          style={{ background: getAvatarCor(u.id) }}
                        >
                          {getIniciais(u.nome)}
                        </div>
                        <span className={styles.rankingNome}>{u.nome}</span>
                        <div className={styles.rankingBar}>
                          <div
                            className={styles.rankingBarFill}
                            style={{
                              width: `${Math.round((u.total / (estatisticas.porUsuario[0]?.total || 1)) * 100)}%`
                            }}
                          />
                        </div>
                        <span className={styles.rankingTotal}>{u.total.toLocaleString('pt-BR')}</span>
                      </div>
                    ))}
                    {!estatisticas.porUsuario?.length && (
                      <div className={styles.semDados}>Sem dados ainda</div>
                    )}
                  </div>
                </div>

                {/* Canais mais ativos */}
                <div className={styles.statsSecao}>
                  <div className={styles.statsSecaoTitulo}>🔥 Canais Mais Ativos</div>
                  <div className={styles.rankingLista}>
                    {(estatisticas.canaisAtivos || []).map((c, i) => (
                      <div key={i} className={styles.rankingItem} id={`rank-canal-${i}`}>
                        <span className={styles.rankingPos}>#{i + 1}</span>
                        <span className={styles.rankingCanalIcon}>{c.tipo === 'dm' ? '💬' : (c.icone || '📢')}</span>
                        <span className={styles.rankingNome}>
                          {c.tipo === 'dm' ? 'Conversa DM' : `#${c.nome}`}
                        </span>
                        <div className={styles.rankingBar}>
                          <div
                            className={styles.rankingBarFill}
                            style={{
                              width: `${Math.round((c.total / (estatisticas.canaisAtivos[0]?.total || 1)) * 100)}%`,
                              background: 'linear-gradient(90deg, #06b6d4, #3b82f6)'
                            }}
                          />
                        </div>
                        <span className={styles.rankingTotal}>{c.total.toLocaleString('pt-BR')}</span>
                      </div>
                    ))}
                    {!estatisticas.canaisAtivos?.length && (
                      <div className={styles.semDados}>Sem dados ainda</div>
                    )}
                  </div>
                </div>

                {/* Atividade últimos 7 dias */}
                <div className={`${styles.statsSecao} ${styles.statsSecaoFull}`}>
                  <div className={styles.statsSecaoTitulo}>📅 Atividade — Últimos 7 dias</div>
                  <div className={styles.graficoBarras}>
                    {(() => {
                      const maxVal = Math.max(...(estatisticas.porDia || []).map(d => d.total), 1);
                      const dias7 = [];
                      for (let i = 6; i >= 0; i--) {
                        const d = new Date(); d.setDate(d.getDate() - i);
                        const iso = fmtDataISO(d);
                        const encontrado = (estatisticas.porDia || []).find(x => x.dia === iso);
                        dias7.push({ iso, total: encontrado?.total || 0 });
                      }
                      return dias7.map(({ iso, total }) => (
                        <div key={iso} className={styles.graficoColuna} id={`grafico-${iso}`}>
                          <span className={styles.graficoValor}>{total}</span>
                          <div
                            className={styles.graficoBar}
                            style={{ height: `${Math.max(4, Math.round((total / maxVal) * 120))}px` }}
                          />
                          <span className={styles.graficoDia}>
                            {new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit' })}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* LIGHTBOX */}
      {imagemZoom && (
        <div className={styles.lightbox} onClick={() => setImagemZoom(null)}>
          <img src={imagemZoom} alt="Zoom" className={styles.lightboxImg} />
          <button className={styles.lightboxFechar} onClick={() => setImagemZoom(null)}>✕</button>
        </div>
      )}
    </div>
  );
};

export default ChatHistoricoAdmin;
